/**
 * Import CSV (Tables view, editors): a file, its columns mapped to the table's (by name first; a column can be
 * skipped), a hint where values do not fit a column's type, a preview of the first rows, then "Stage N rows": they
 * join the pending changes as new rows, reviewed (each row checked) and applied in one transaction like any other.
 */
import React, { useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { UploadSimple, Warning } from '@phosphor-icons/react';
import { DataDialog } from './DataCommon';
import { formatCount } from '../../utils/dataModel';
import { CSV_MAX_ROWS, SKIP, autoMap, csvInserts, missingRequired, parseCsv, typeIssues } from '../../utils/dataImport';

const PREVIEW_ROWS = 5;
const DELIMITERS = [[',', 'Comma'], [';', 'Semicolon'], ['\t', 'Tab'], ['|', 'Pipe']];

const readText = (file) => (typeof file.text === 'function' ? file.text() : new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result || ''));
  reader.onerror = () => reject(reader.error);
  reader.readAsText(file);
}));

export default function CsvImportDialog({ table, columns, onStage, onClose }) {
  const [file, setFile] = useState(null); // { name, text }
  const [readError, setReadError] = useState(null);
  const [delimiter, setDelimiter] = useState(null);
  const [header, setHeader] = useState(true);
  const [mapping, setMapping] = useState([]);
  const pickRef = useRef(null);
  const writable = columns.filter((c) => !c.generated);

  const parsed = useMemo(() => (file ? parseCsv(file.text, { delimiter: delimiter || undefined, maxRows: CSV_MAX_ROWS + 1 }) : null), [file, delimiter]);
  const rows = parsed?.rows || [];
  const width = Math.max(0, ...rows.slice(0, 50).map((r) => r.length));
  const headers = header && rows.length ? rows[0].map((h, i) => h || `column ${i + 1}`) : Array.from({ length: width }, (_, i) => `column ${i + 1}`);
  const tooMany = rows.length - (header ? 1 : 0) > CSV_MAX_ROWS;
  const kept = useMemo(() => (header ? rows.slice(1) : rows).slice(0, CSV_MAX_ROWS), [rows, header]);

  const load = async (picked) => {
    if (!picked) { return; }
    setReadError(null);
    try {
      const text = await readText(picked);
      const first = parseCsv(text, { maxRows: 1 });
      setFile({ name: picked.name, text });
      setDelimiter(first.delimiter);
      setMapping(autoMap((first.rows[0] || []).map(String), writable));
    } catch (e) {
      setReadError(`Could not read the file: ${e?.message || e}`);
    }
  };

  const toggleHeader = (on) => {
    setHeader(on);
    setMapping(on && rows[0] ? autoMap(rows[0].map(String), writable) : headers.map(() => SKIP));
  };
  const changeDelimiter = (next) => {
    setDelimiter(next);
    const first = parseCsv(file.text, { delimiter: next, maxRows: 1 });
    setMapping(header ? autoMap((first.rows[0] || []).map(String), writable) : (first.rows[0] || []).map(() => SKIP));
  };
  const mapTo = (index, column) => setMapping((prev) => {
    const next = headers.map((_, i) => prev[i] ?? SKIP);
    // A table column takes one CSV column: the one it had before goes to Skip.
    if (column) { next.forEach((c, i) => { if (c === column) { next[i] = SKIP; } }); }
    next[index] = column;
    return next;
  });

  const full = headers.map((_, i) => mapping[i] ?? SKIP);
  const inserts = useMemo(() => csvInserts(kept, full, { firstLine: header ? 2 : 1 }), [kept, full.join('\u0000'), header]); // eslint-disable-line react-hooks/exhaustive-deps
  const issues = useMemo(() => typeIssues(inserts, columns), [inserts, columns]);
  const missing = missingRequired(full, columns);
  const mappedCount = full.filter(Boolean).length;
  const usable = inserts.filter((ins) => Object.keys(ins.values).length > 0);
  const byName = Object.fromEntries(columns.map((c) => [c.name, c]));

  return (
    <DataDialog
      footer={(
        <>
          <span className="data-muted">{file ? `${formatCount(usable.length)} row${usable.length === 1 ? '' : 's'} to add${tooMany ? ` (the first ${formatCount(CSV_MAX_ROWS)})` : ''}` : ''}</span>
          <span className="data-grow" />
          <button className="data-btn" onClick={onClose} type="button">Cancel</button>
          <button
            className="data-btn is-primary"
            data-testid="csv-stage"
            disabled={!file || !mappedCount || !usable.length}
            onClick={() => onStage({ file: file.name, rows: usable })}
            type="button"
          >
            Stage {formatCount(usable.length)} row{usable.length === 1 ? '' : 's'}
          </button>
        </>
      )}
      onClose={onClose}
      subtitle={<>New rows for <strong>{table}</strong>: they wait with your other changes until you review and apply them.</>}
      testId="csv-import"
      title={`Import CSV into ${table}`}
      wide
    >
      <div className="data-row-gap is-wrap">
        <input accept=".csv,.tsv,.txt,text/csv,text/tab-separated-values" className="data-sr" data-testid="csv-file" id="data-csv-file" onChange={(e) => load(e.target.files?.[0])} ref={pickRef} type="file" />
        <button className="data-btn" data-autofocus onClick={() => pickRef.current?.click()} type="button"><UploadSimple aria-hidden size={15} />{file ? 'Another file…' : 'Choose a CSV file…'}</button>
        {file && <span className="data-mono">{file.name}</span>}
        {file && (
          <>
            <label className="data-label" htmlFor="data-csv-delimiter">Separator</label>
            <select className="data-input" id="data-csv-delimiter" onChange={(e) => changeDelimiter(e.target.value)} value={parsed?.delimiter || ','}>
              {DELIMITERS.map(([value, label]) => <option key={label} value={value}>{label}</option>)}
            </select>
            <label className="data-record-check"><input checked={header} onChange={(e) => toggleHeader(e.target.checked)} type="checkbox" />First row is the header</label>
          </>
        )}
      </div>
      {readError && <p className="data-error-text" role="alert">{readError}</p>}
      {!file && <p className="data-footnote">Columns are matched to the table&apos;s by name. Empty cells take the column&apos;s default (or NULL). At most {formatCount(CSV_MAX_ROWS)} rows at a time.</p>}
      {file && !rows.length && <p className="data-error-text" role="alert">The file has no rows.</p>}
      {tooMany && <p className="data-banner" role="status"><Warning aria-hidden size={15} />The file has more than {formatCount(CSV_MAX_ROWS)} rows: the first {formatCount(CSV_MAX_ROWS)} are staged. Import the rest in a second file.</p>}

      {file && rows.length > 0 && (
        <>
          <div className="data-table-wrap">
            <table className="data-table" data-testid="csv-mapping">
              <thead>
                <tr>
                  <th scope="col">CSV column</th>
                  <th scope="col">Goes to</th>
                  <th scope="col">Type</th>
                  <th scope="col">Check</th>
                </tr>
              </thead>
              <tbody>
                {headers.map((h, i) => {
                  const column = full[i];
                  const issue = column && issues[column];
                  return (
                    <tr key={`${h}-${i}`}>
                      <td className="data-mono">{h}</td>
                      <td>
                        <label className="data-sr" htmlFor={`data-csv-map-${i}`}>{`Column for ${h}`}</label>
                        <select className="data-input" id={`data-csv-map-${i}`} onChange={(e) => mapTo(i, e.target.value)} value={column}>
                          <option value={SKIP}>Skip this column</option>
                          {writable.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
                        </select>
                      </td>
                      <td className="data-muted">{column ? `${byName[column]?.type || ''}${byName[column]?.pk ? ' · key' : ''}` : ''}</td>
                      <td data-testid="csv-issue">
                        {issue
                          ? <span className="data-pill is-warn" title={issue.first.map((f) => `line ${f.line}: “${f.value}” is ${f.issue}`).join('\n')}>{formatCount(issue.count)} {issue.first[0].issue} (line {issue.first[0].line})</span>
                          : column ? <span className="data-pill is-ok">fits</span> : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {missing.length > 0 && (
            <p className="data-banner" data-testid="csv-missing" role="status"><Warning aria-hidden size={15} />{missing.join(', ')} {missing.length === 1 ? 'needs' : 'need'} a value and {missing.length === 1 ? 'has' : 'have'} no default: map {missing.length === 1 ? 'it' : 'them'}, or those rows will fail.</p>
          )}
          <div>
            <div className="data-label">Preview: the first {Math.min(PREVIEW_ROWS, usable.length)} of {formatCount(usable.length)} rows</div>
            <div className="data-table-wrap">
              <table className="data-table" data-testid="csv-preview">
                <thead><tr><th scope="col">line</th>{full.filter(Boolean).map((c) => <th key={c} scope="col">{c}</th>)}</tr></thead>
                <tbody>
                  {usable.slice(0, PREVIEW_ROWS).map((ins) => (
                    <tr key={ins.line}>
                      <td className="data-muted">{ins.line}</td>
                      {full.filter(Boolean).map((c) => <td className={ins.values[c] === undefined ? 'is-null' : ''} key={c}>{ins.values[c] ?? 'default'}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </DataDialog>
  );
}

CsvImportDialog.propTypes = {
  columns: PropTypes.array.isRequired,
  onClose: PropTypes.func.isRequired,
  onStage: PropTypes.func.isRequired,
  table: PropTypes.string.isRequired,
};
